import { useEffect, useState } from 'react';

export default function Hero() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Minimal timeout — just enough for first paint to complete
    const t = setTimeout(() => setLoaded(true), 50);
    return () => clearTimeout(t);
  }, []);

  const fade = (delay: number): React.CSSProperties => ({
    opacity: loaded ? 1 : 0,
    transform: loaded ? 'translateY(0)' : 'translateY(20px)',
    transition: `opacity 0.65s ease ${delay}s, transform 0.65s ease ${delay}s`,
  });

  return (
    <header id="hero" className="relative flex flex-col overflow-hidden bg-[#0a0a0a]">

      {/* ── Background layers ── */}
      <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #c8a96e 1px, transparent 0)',
            backgroundSize: '36px 36px',
          }}
        />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-[#c8a96e]/5 blur-[140px]" />
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
        <div
          className="absolute left-0 top-0 bottom-0 w-[2px] hidden lg:block"
          style={{ background: 'linear-gradient(to bottom, transparent, #c8a96e 25%, #c8a96e 75%, transparent)' }}
        />
      </div>

      {/* ── Main content ── */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-5 sm:px-10 lg:px-16 pt-28 sm:pt-36 pb-14 sm:pb-20 text-center">

        {/* Badge */}
        <div style={fade(0.0)} className="inline-flex items-center gap-2 bg-[#c8a96e]/10 border border-[#c8a96e]/30 rounded-sm px-4 py-2 mb-6 sm:mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-[#c8a96e] flex-shrink-0" style={{ boxShadow: '0 0 8px 2px rgba(200,169,110,0.7)' }} />
          <span className="text-[#c8a96e] text-[10px] sm:text-xs font-black tracking-[0.22em] uppercase">Real Results. Real System. No Gimmicks.</span>
        </div>

        {/* H1 */}
        <h1
          className="text-4xl sm:text-6xl lg:text-7xl font-black text-white leading-[1.02] tracking-tight mb-4 sm:mb-5"
          style={fade(0.1)}
        >
          <strong className="block">I Got In Shape</strong>
          <strong className="block">At 62 Years Old.</strong>
          <span
            className="block mt-2 sm:mt-3"
            style={{
              background: 'linear-gradient(90deg, #c8a96e 0%, #e8d5a3 50%, #c8a96e 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            You Can Too.
          </span>
        </h1>

        {/* Subheadline */}
        <p
          className="text-sm sm:text-base lg:text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto mb-8 sm:mb-10 font-light"
          style={fade(0.18)}
        >
          At 60, I was <strong className="text-white font-semibold">240 lbs</strong> and struggling.
          Over 2 years, I <strong className="text-white font-semibold">lost 55 lbs</strong> and completely transformed my body.
          Here's the proof — and the{' '}
          <strong className="text-white font-semibold">over-60 strength training program</strong> that made it possible.
        </p>

        {/* ── BEFORE / AFTER — Hero Centerpiece ── */}
        <div className="w-full mb-8 sm:mb-10" style={fade(0.26)}>
          {/* Proof label */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="flex-1 h-px bg-[#c8a96e]/20 max-w-[80px] sm:max-w-[140px]" />
            <span className="text-[#c8a96e] text-[9px] sm:text-[10px] tracking-[0.25em] uppercase font-black whitespace-nowrap">
              The Transformation
            </span>
            <div className="flex-1 h-px bg-[#c8a96e]/20 max-w-[80px] sm:max-w-[140px]" />
          </div>

          {/* Card */}
          <div
            className="mx-auto rounded-sm overflow-hidden border border-[#c8a96e]/25"
            style={{
              maxWidth: '720px',
              boxShadow: '0 0 80px 0 rgba(200,169,110,0.12), 0 0 0 1px rgba(200,169,110,0.08)',
            }}
          >
            {/* Images row */}
            <div className="flex" style={{ height: 'clamp(260px, 52vw, 500px)' }}>

              {/* Before */}
              <div className="relative flex-1 overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/388360cd24ebfe3438e68a0ccf7fe406/5540043e75a4496a39b56ba7a23282ab.png"
                  alt="Mason Hale before fitness transformation — age 60, 240 lbs, before senior strength training program"
                  title="Mason Hale Before — Age 60, 240 lbs"
                  width="600"
                  height="800"
                  className="w-full h-full object-cover object-top"
                  loading="eager"
                  decoding="sync"
                  fetchPriority="high"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/15 to-transparent" />
                <div className="absolute top-3 left-3">
                  <div className="text-white text-[9px] sm:text-[10px] font-black tracking-[0.18em] uppercase bg-black/75 border border-white/20 px-2.5 py-1 rounded-sm">
                    Before
                  </div>
                </div>
                <div className="absolute bottom-3 left-3">
                  <div className="text-gray-200 text-[11px] sm:text-xs font-bold">Age 60 · 240 lbs</div>
                </div>
              </div>

              {/* Divider */}
              <div className="relative flex-shrink-0 w-px bg-[#c8a96e]/60 z-10">
                <div
                  className="absolute z-20 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#c8a96e] flex items-center justify-center"
                  style={{
                    left: '50%', top: '50%',
                    transform: 'translate(-50%, -50%)',
                    boxShadow: '0 0 24px 8px rgba(200,169,110,0.55)',
                  }}
                >
                  <i className="ri-arrow-left-right-line text-black text-sm" aria-hidden="true" />
                </div>
              </div>

              {/* After */}
              <div className="relative flex-1 overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/388360cd24ebfe3438e68a0ccf7fe406/32443206f1de05fae87c4486daae2dfb.png"
                  alt="Mason Hale after fitness transformation — age 62, 185 lbs, results from strength training program for men over 60"
                  title="Mason Hale After — Age 62, 185 lbs"
                  width="600"
                  height="800"
                  className="w-full h-full object-cover object-top"
                  loading="eager"
                  decoding="sync"
                  fetchPriority="high"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/15 to-transparent" />
                <div className="absolute top-3 right-3">
                  <div className="text-black text-[9px] sm:text-[10px] font-black tracking-[0.18em] uppercase bg-[#c8a96e] px-2.5 py-1 rounded-sm">
                    After
                  </div>
                </div>
                <div className="absolute bottom-3 right-3 text-right">
                  <div className="text-[#c8a96e] text-[11px] sm:text-xs font-bold">Age 62 · 185 lbs</div>
                </div>
              </div>
            </div>

            {/* Caption bar */}
            <div className="bg-[#0f0f0f] border-t border-[#c8a96e]/15 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-1.5 sm:gap-0">
              <span className="text-gray-400 text-[10px] tracking-[0.2em] uppercase font-bold">55 lbs Lost · 2 Years · Age 60–62</span>
              <span className="text-[#c8a96e]/60 text-[10px] tracking-[0.2em] uppercase font-bold">No Surgery · No Shortcuts</span>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div
          className="flex flex-wrap items-center justify-center gap-6 sm:gap-14 mb-8 sm:mb-10"
          style={fade(0.36)}
        >
          {[
            { value: '55', suffix: ' lbs', label: 'Weight Lost' },
            { value: '2', suffix: ' yrs', label: 'Transformation' },
            { value: '60', suffix: '+', label: 'Years Old' },
          ].map((stat, i) => (
            <div key={i} className="flex flex-col items-center">
              <div className="flex items-baseline">
                <span className="text-4xl sm:text-5xl font-black text-[#c8a96e]">{stat.value}</span>
                <span className="text-xl sm:text-2xl font-black text-[#c8a96e]/60 ml-1">{stat.suffix}</span>
              </div>
              <span className="text-gray-500 text-[10px] uppercase tracking-widest mt-1 font-semibold">{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="flex items-center justify-center gap-4 mb-8 sm:mb-10" style={fade(0.42)}>
          <div className="h-px w-16 sm:w-24 bg-gradient-to-r from-transparent to-[#c8a96e]/40" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#c8a96e]/50" />
          <div className="h-px w-16 sm:w-24 bg-gradient-to-l from-transparent to-[#c8a96e]/40" />
        </div>

        {/* CTA buttons */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
          style={fade(0.48)}
        >
          <a
            href="#programs"
            onClick={(e) => {
              e.preventDefault();
              const section = document.getElementById('programs');
              if (!section) return;
              const card = section.querySelector('article') as HTMLElement | null;
              const target = card ?? section;
              const rect = target.getBoundingClientRect();
              const centerOffset = rect.top + window.scrollY - (window.innerHeight / 2) + (rect.height / 2);
              window.scrollTo({ top: Math.max(0, centerOffset), behavior: 'smooth' });
            }}
            aria-label="Get the Over-60 Strength Blueprint fitness program for men over 60"
            className="w-full sm:w-auto px-8 sm:px-10 py-4 text-black text-sm font-black tracking-[0.18em] uppercase rounded-sm hover:brightness-110 active:scale-95 transition-all duration-200 whitespace-nowrap cursor-pointer text-center"
            style={{ background: 'linear-gradient(135deg, #c8a96e 0%, #e0c882 50%, #c8a96e 100%)' }}
          >
            Get The Program — $19.99
          </a>
          <a
            href="#story"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById('story');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            aria-label="Read Mason Hale's over-60 fitness transformation story"
            className="w-full sm:w-auto px-8 sm:px-10 py-4 bg-transparent border border-white/20 text-white text-sm font-black tracking-[0.18em] uppercase rounded-sm hover:bg-white/8 hover:border-white/35 active:scale-95 transition-all duration-200 whitespace-nowrap cursor-pointer text-center"
          >
            See The Story
          </a>
        </div>

        {/* Trust micro-copy */}
        <p className="text-gray-600 text-[10px] sm:text-xs tracking-widest uppercase mt-5 font-medium" style={fade(0.54)}>
          Instant PDF Access · No Subscription · One-Time Payment
        </p>
      </div>

      {/* Scroll indicator */}
      <div className="relative z-10 flex flex-col items-center gap-2 pb-10" aria-hidden="true">
        <span className="text-gray-600 text-[10px] tracking-[0.3em] uppercase font-semibold">Scroll to see the story</span>
        <div style={{ animation: 'bounceScroll 1.8s ease-in-out infinite' }}>
          <div className="w-5 h-8 rounded-full border border-white/20 flex items-start justify-center pt-1.5">
            <div className="w-1 h-2 rounded-full bg-[#c8a96e]" style={{ animation: 'scrollDot 1.8s ease-in-out infinite' }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bounceScroll {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(6px); }
        }
        @keyframes scrollDot {
          0%   { transform: translateY(0);    opacity: 1; }
          80%  { transform: translateY(10px); opacity: 0; }
          100% { transform: translateY(0);    opacity: 0; }
        }
      `}</style>
    </header>
  );
}
